import { useState, useEffect } from 'react';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';
import client from '../../api/client';
import { NAVY, AMBER, BORDER_COLOR, BG_FIELD, bubbleStyle, fieldStyle, navyBtn } from '../../styles/tokens';

const EVENT_LABELS = {
  'auth.login_success': { label: 'Inicio de sesión', color: '#16a34a' },
  'auth.login_failure': { label: 'Login fallido', color: '#ef4444' },
  'receipt_item.approve': { label: 'Gasto aprobado', color: '#16a34a' },
  'receipt_item.return': { label: 'Gasto devuelto', color: '#f59e0b' },
  'receipt_item.reject': { label: 'Gasto rechazado', color: '#ef4444' },
  'receipt.final_approve': { label: 'Gasto aprobado (final)', color: '#16a34a' },
  'receipt.final_return': { label: 'Gasto devuelto (final)', color: '#f59e0b' },
  'daily_sheet.submit': { label: 'Tarjeta enviada', color: '#2563eb' },
  'daily_sheet.approve': { label: 'Tarjeta aprobada', color: '#16a34a' },
  'daily_sheet.return': { label: 'Tarjeta devuelta', color: '#f59e0b' },
  'user.create': { label: 'Usuario creado', color: '#7c3aed' },
  'user.update': { label: 'Usuario modificado', color: '#7c3aed' },
};

const ENTITY_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'receipt', label: 'Gastos' },
  { value: 'daily_sheet', label: 'Nómina' },
  { value: 'user', label: 'Usuarios' },
];

function formatDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('es-PR', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' ' + dt.toLocaleTimeString('es-PR', { hour: '2-digit', minute: '2-digit' });
}

export default function AuditLogViewer() {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const limit = 30;

  // Filters
  const today = new Date().toISOString().slice(0, 10);
  const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
  const [entityType, setEntityType] = useState('');
  const [from, setFrom] = useState(weekAgo);
  const [to, setTo] = useState(today);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => { fetchLogs(); }, [offset]); // eslint-disable-line react-hooks/exhaustive-deps

  function fetchLogs(resetOffset) {
    if (resetOffset) setOffset(0);
    setLoading(true);
    const params = { limit, offset: resetOffset ? 0 : offset };
    if (entityType) params.entity_type = entityType;
    if (from) params.from = from;
    if (to) params.to = to;
    client.get('/api/admin/audit-log', { params })
      .then(r => { setEvents(r.data.events || []); setTotal(r.data.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  const pages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <div>
      {/* Filter bar */}
      <div style={{ ...bubbleStyle, padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Módulo</div>
          <select value={entityType} onChange={e => setEntityType(e.target.value)}
            style={{ ...fieldStyle, padding: '5px 8px', fontSize: 12, width: 'auto', minWidth: 140 }}>
            {ENTITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Desde</div>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)}
            style={{ ...fieldStyle, padding: '5px 8px', fontSize: 12, width: 'auto' }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Hasta</div>
          <input type="date" value={to} onChange={e => setTo(e.target.value)}
            style={{ ...fieldStyle, padding: '5px 8px', fontSize: 12, width: 'auto' }} />
        </div>
        <button onClick={() => fetchLogs(true)} disabled={loading} style={{ ...navyBtn, padding: '7px 16px', fontSize: 12 }}>
          <Search size={13} style={{ marginRight: 4, verticalAlign: -2 }} />
          {loading ? 'Buscando...' : 'Buscar'}
        </button>
      </div>

      {/* Summary */}
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
        {total} evento{total !== 1 ? 's' : ''} encontrado{total !== 1 ? 's' : ''}
        {pages > 1 && ` · Página ${currentPage} de ${pages}`}
      </div>

      {/* Events table */}
      <div style={bubbleStyle}>
        {/* Header */}
        <div style={{
          display: 'grid', gridTemplateColumns: '160px 1fr 140px 1fr',
          gap: 8, padding: '0 0 10px', borderBottom: `1px solid ${BORDER_COLOR}`,
          fontSize: 10, fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em',
        }}>
          <span>Fecha</span><span>Evento</span><span>Usuario</span><span>Detalle</span>
        </div>

        {events.length === 0 && !loading && (
          <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
            No hay eventos para los filtros seleccionados
          </div>
        )}

        {events.map(ev => {
          const cfg = EVENT_LABELS[ev.event_type] || { label: ev.event_type, color: '#64748b' };
          const isOpen = expandedId === ev.id;
          const hasDetails = ev.old_values || ev.new_values || ev.metadata;

          return (
            <div key={ev.id} style={{ borderBottom: `1px solid ${BORDER_COLOR}` }}>
              <div
                onClick={() => hasDetails && setExpandedId(isOpen ? null : ev.id)}
                style={{
                  display: 'grid', gridTemplateColumns: '160px 1fr 140px 1fr',
                  gap: 8, padding: '10px 0', alignItems: 'center', fontSize: 12,
                  cursor: hasDetails ? 'pointer' : 'default',
                }}
              >
                <span style={{ color: '#475569', fontSize: 11 }}>{formatDate(ev.created_at)}</span>
                <span>
                  <span style={{
                    display: 'inline-block', padding: '2px 8px', borderRadius: 10,
                    fontSize: 10, fontWeight: 600, background: cfg.color + '18', color: cfg.color,
                    border: `1px solid ${cfg.color}30`,
                  }}>{cfg.label}</span>
                  {ev.entity_id && (
                    <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 6 }}>#{ev.entity_id}</span>
                  )}
                </span>
                <span style={{ color: '#475569', fontSize: 11 }}>{ev.actor_name || ev.actor_email || '—'}</span>
                <span style={{ color: '#94a3b8', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {ev.new_values?.status && `→ ${ev.new_values.status}`}
                  {ev.new_values?.reason && ` (${ev.new_values.reason})`}
                  {ev.metadata?.ip && !ev.new_values?.status && `IP: ${ev.metadata.ip}`}
                </span>
              </div>

              {/* Expanded details */}
              {isOpen && hasDetails && (
                <div style={{
                  padding: '8px 14px 12px', background: BG_FIELD, borderRadius: 8,
                  marginBottom: 8, fontSize: 11, fontFamily: 'monospace',
                }}>
                  {ev.old_values && (
                    <div style={{ marginBottom: 6 }}>
                      <span style={{ fontWeight: 700, color: '#ef4444' }}>Anterior: </span>
                      <span style={{ color: '#475569' }}>{JSON.stringify(ev.old_values)}</span>
                    </div>
                  )}
                  {ev.new_values && (
                    <div style={{ marginBottom: 6 }}>
                      <span style={{ fontWeight: 700, color: '#16a34a' }}>Nuevo: </span>
                      <span style={{ color: '#475569' }}>{JSON.stringify(ev.new_values)}</span>
                    </div>
                  )}
                  {ev.metadata && (
                    <div>
                      <span style={{ fontWeight: 700, color: '#64748b' }}>Metadata: </span>
                      <span style={{ color: '#475569' }}>{JSON.stringify(ev.metadata)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 14, alignItems: 'center' }}>
          <button
            onClick={() => setOffset(Math.max(0, offset - limit))}
            disabled={offset === 0}
            style={{
              background: 'none', border: `1px solid ${BORDER_COLOR}`, borderRadius: 6,
              padding: '4px 10px', cursor: offset === 0 ? 'not-allowed' : 'pointer',
              opacity: offset === 0 ? 0.4 : 1, display: 'flex', alignItems: 'center',
            }}
          >
            <ChevronLeft size={14} />
          </button>
          <span style={{ fontSize: 12, color: '#64748b' }}>
            Página {currentPage} de {pages}
          </span>
          <button
            onClick={() => setOffset(offset + limit)}
            disabled={offset + limit >= total}
            style={{
              background: 'none', border: `1px solid ${BORDER_COLOR}`, borderRadius: 6,
              padding: '4px 10px', cursor: offset + limit >= total ? 'not-allowed' : 'pointer',
              opacity: offset + limit >= total ? 0.4 : 1, display: 'flex', alignItems: 'center',
            }}
          >
            <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
